import { Button, Icon, InputGroup, Spinner } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomTooltip from "components/Atoms/CustomTooltip"
import Divider from "components/Atoms/Divider"
import Toggle from "components/Atoms/Toggle"
import Map from "components/Organisms/map/Map"
import ReOrderList from "components/Organisms/ReOrderList"
import { COLOURS } from "data/static.items"
import { RouteDetailQuery } from "generated/graphql"
import { pick } from "lodash-es"
import React, { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import classNames from "utils/classNames"
import Format from "utils/Format"
import { paleMap } from "utils/mapStyles"
import { Locations } from "utils/symbols.utils"
import useRouteStore from "../../routes.store"
import OutletSelectAll from "./OutletDeSelectAll"
import OutletInfoCard from "./OutletInfoCard"
import OutletList from "./OutletList"
import OutletMapContent from "./OutletMapContent"
import RouteInfo from "./RouteInfo"

type RouteDetailViewType = {
  route?: RouteDetailQuery["routeDetail"]
  className?: string
  showRouteInfo?: boolean
  children?: React.ReactNode
  notFoundMessage?: string
  fetchData?: boolean
  disabled?: boolean
}
const RouteDetailView = ({
  route,
  className,
  showRouteInfo = true,
  fetchData,
  notFoundMessage,
  children,
  disabled,
}: RouteDetailViewType) => {
  const [params, setParams] = useSearchParams()
  const search = params.get("search")
  const view = params.get("view")
  const store = useRouteStore()
  const state = store.data
  const filteredOutlets = store.querySelectedOutlets(search)

  useEffect(() => {
    if (route?.id) {
      const selectedOutlets = route.points?.map?.(point => ({
        sequence: point.sequence,
        ...point.visitee,
      }))
      store.initialiseState({
        ...pick(route, ["id", "isActive", "name", "category"]),
        outlets: [],
        surveyArea: route.surveyArea,
        colorCode: COLOURS.find(c => c.id === route.colorCode),
        optimizeRoute: true,
        selectedOutlets: selectedOutlets?.length ? selectedOutlets : [],
        step: "select",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route])

  return (
    <div
      className={classNames("p-0 flex h-[calc(100vh-48px-64px)]", className)}
    >
      <Map
        styles={paleMap}
        loadingElement={
          <div className="flex justify-center items-center h-full w-full">
            <Spinner />
          </div>
        }
        mapContainerClassName="h-full"
        center={Locations.Mohadi}
        zoom={15}
      >
        <OutletMapContent disabled={disabled} />
      </Map>
      <div className="shadow-sm bg-gray-100 w-3/12 z-[5] rounded-l-xl rounded-b-xl flex flex-col flex-nowrap">
        <header className="bg-white border-b py-[0.75rem] px-2 flex items-center justify-between">
          <h6 className="m-0">
            {state.step === "select" ? null : (
              <CustomButton
                small
                icon="arrow-left"
                minimal
                className="mr-1"
                onClick={() => store.prevStep()}
              />
            )}
            Outlet Section
          </h6>
        </header>
        <Divider className="my-0" />
        {showRouteInfo && (
          <RouteInfo isReadOnly={store.data.step === "order"} />
        )}
        <section
          id="fsm-outlet-list"
          className="flex h-full flex-col overflow-x-hidden overflow-y-auto"
        >
          <header
            id="fsm-outlet-list-header"
            className="bg-white font-sm flex flex-col mt-2"
          >
            <div className="flex justify-between items-center p-2">
              <h6 className="my-0">
                <Icon className="mr-2" icon={"asterisk"} />
                Outlet List ({store.data.selectedOutlets.length})
              </h6>
              {state.step === "select" ? (
                <OutletSelectAll />
              ) : (
                <CustomTooltip
                  hoverOpenDelay={500}
                  content={
                    <div className="max-w-xs">
                      Automatically arrange outlets as per our recommendation
                      (user will not be allowed to change the order manually)
                    </div>
                  }
                >
                  <Toggle
                    onChange={e =>
                      store.setData("optimizeRoute", e.currentTarget.checked)
                    }
                    checked={state.optimizeRoute}
                    className="mb-0 text-xs"
                    label="Auto arrange outlets"
                  />
                </CustomTooltip>
              )}
            </div>
            {state?.step !== "select" && state.optimizeRoute && (
              <p className="m-0 p-2 text-xs">
                First outlet will be the <b>source</b> and the last outlet will
                be the <b>destination</b>
              </p>
            )}
            <Divider className="my-0" />
            <InputGroup
              value={search}
              onChange={e => setParams({ search: e.target.value, view })}
              className="m-2"
              placeholder="Search by name or number"
              leftIcon="search"
              rightElement={
                <Button
                  onClick={() => setParams({ search: "", view })}
                  icon="cross"
                  intent="danger"
                  minimal
                />
              }
            />
            {state.googleMapRoute && (
              <p id="fsm-trip-info" className="mb-0 text-sm text-gray-700 p-2">
                Distance:{" "}
                <span className="text-gray-800 font-500">
                  {Format.distance(state.googleMapRoute.totalDistance / 1000)}
                </span>
                , Duration:{" "}
                <span className="text-gray-800 font-500">
                  {Format.secondsToHoursAndMinutes(
                    state.googleMapRoute.totalDuration
                  )}
                </span>
              </p>
            )}
          </header>
          <div className="grow p-2 overflow-auto">
            {state.step === "select" ? (
              <OutletList
                disabled={disabled}
                notFoundMessage={notFoundMessage}
                fetchData={fetchData}
              />
            ) : (
              <ReOrderList
                id="selected_outlets_markers"
                disabled={state.optimizeRoute}
                staleClassName="bg-white"
                draggableClassName="rounded mb-2 shadow-sm select-none"
                list={filteredOutlets}
                renderItem={(item, index) => {
                  const sequence = index + 1

                  return (
                    <OutletInfoCard
                      isSelectable={false}
                      outlet={item}
                      sequence={sequence}
                    />
                  )
                }}
                onSequenceChange={list => {
                  store.setData("selectedOutlets", list)
                }}
              />
            )}
          </div>
        </section>
        {children}
      </div>
    </div>
  )
}

export default RouteDetailView
